import * as THREE from 'three'
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js'
import { createCameraModel, DEFAULT_FOV_Y_DEG, type CameraModel, type Pose } from '@ar-suite/core'

export interface ARSceneOptions {
  /** Vertical FOV in degrees — must match the tracker's camera model (MediaPipe: 63). */
  fovYDeg: number
  /** Mirror the rendered output horizontally (selfie camera). */
  mirror: boolean
  /** Device pixel ratio cap for the drawing buffer. */
  maxPixelRatio: number
  antialias: boolean
  /** Existing canvas to render into (else one is created). */
  canvas?: HTMLCanvasElement
  /** Add an image-based lighting environment (RoomEnvironment). */
  environment: boolean
  /** Global IBL intensity (RoomEnvironment is bright; ~0.6 reads naturally over camera video). */
  environmentIntensity: number
  toneMappingExposure: number
}

export const AR_SCENE_DEFAULTS: ARSceneOptions = {
  fovYDeg: DEFAULT_FOV_Y_DEG,
  mirror: true,
  maxPixelRatio: 2,
  antialias: true,
  environment: true,
  environmentIntensity: 0.6,
  toneMappingExposure: 1.0,
}

/** Common overlay styling for the video and the render canvas: fill the container, crop identically. */
export function styleAsOverlay(el: HTMLElement, mirror: boolean): void {
  Object.assign(el.style, {
    position: 'absolute',
    inset: '0',
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transform: mirror ? 'scaleX(-1)' : 'none',
    transformOrigin: 'center',
  } as Partial<CSSStyleDeclaration>)
}

/**
 * three.js scene whose camera matches the tracker's pinhole model so camera-space poses (cm)
 * can be applied directly. The canvas is transparent and sized to the video frame so it overlays 1:1.
 */
export class ARScene {
  readonly options: ARSceneOptions
  readonly renderer: THREE.WebGLRenderer
  readonly scene: THREE.Scene
  readonly camera: THREE.PerspectiveCamera
  /** Wearables go here. */
  readonly content: THREE.Group
  /** Occluders go here (rendered first, depth only). */
  readonly occluders: THREE.Group
  readonly canvas: HTMLCanvasElement
  readonly keyLight: THREE.DirectionalLight
  private cameraModel: CameraModel
  private pmrem: THREE.PMREMGenerator | null = null
  private envTarget: THREE.WebGLRenderTarget | null = null
  private _width = 1280
  private _height = 720

  constructor(options: Partial<ARSceneOptions> = {}) {
    this.options = { ...AR_SCENE_DEFAULTS, ...options }
    this.canvas = this.options.canvas ?? document.createElement('canvas')
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      alpha: true,
      antialias: this.options.antialias,
      premultipliedAlpha: true,
      powerPreference: 'high-performance',
    })
    this.renderer.setClearColor(0x000000, 0)
    this.renderer.outputColorSpace = THREE.SRGBColorSpace
    this.renderer.toneMapping = THREE.NeutralToneMapping
    this.renderer.toneMappingExposure = this.options.toneMappingExposure
    this.renderer.autoClear = true
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, this.options.maxPixelRatio))

    this.scene = new THREE.Scene()
    this.camera = new THREE.PerspectiveCamera(this.options.fovYDeg, 16 / 9, 1, 10000)
    this.camera.position.set(0, 0, 0)
    this.camera.lookAt(0, 0, -1)
    this.cameraModel = createCameraModel(16 / 9, this.options.fovYDeg)

    this.occluders = new THREE.Group()
    this.occluders.name = 'occluders'
    this.occluders.renderOrder = -100
    this.content = new THREE.Group()
    this.content.name = 'content'
    this.scene.add(this.occluders, this.content)

    // Lighting: IBL for PBR + a key light for crisp speculars on rims/metal.
    this.keyLight = new THREE.DirectionalLight(0xffffff, 1.2)
    this.keyLight.position.set(20, 40, 60)
    this.scene.add(this.keyLight)
    this.scene.add(new THREE.HemisphereLight(0xffffff, 0x666666, 0.4))
    if (this.options.environment) this.setEnvironment('room')
    this.scene.environmentIntensity = this.options.environmentIntensity

    this.setMirror(this.options.mirror)
    this.resize(this._width, this._height)
  }

  /** Camera model that matches this scene's camera (share with solvers). */
  get model(): CameraModel {
    return this.cameraModel
  }
  get width(): number {
    return this._width
  }
  get height(): number {
    return this._height
  }

  /** Attach the render canvas over a video inside a container. */
  mount(container: HTMLElement, video?: HTMLVideoElement): void {
    const cs = getComputedStyle(container)
    if (cs.position === 'static') container.style.position = 'relative'
    if (!container.style.overflow) container.style.overflow = 'hidden'
    if (video) {
      styleAsOverlay(video, this.options.mirror)
      if (video.parentElement !== container) container.appendChild(video)
    }
    styleAsOverlay(this.canvas, this.options.mirror)
    this.canvas.style.pointerEvents = 'none'
    if (this.canvas.parentElement !== container) container.appendChild(this.canvas)
  }

  setMirror(mirror: boolean): void {
    this.options.mirror = mirror
    this.canvas.style.transform = mirror ? 'scaleX(-1)' : 'none'
  }

  /** Set the frame size (video intrinsic size). The canvas drawing buffer follows it (× pixel ratio). */
  resize(width: number, height: number): void {
    if (width <= 0 || height <= 0) return
    this._width = width
    this._height = height
    this.renderer.setSize(width, height, false)
    this.camera.aspect = width / height
    this.camera.updateProjectionMatrix()
    this.cameraModel = createCameraModel(width / height, this.options.fovYDeg)
  }

  setEnvironment(kind: 'room' | 'none'): void {
    if (this.envTarget) {
      this.envTarget.dispose()
      this.envTarget = null
    }
    if (kind === 'none') {
      this.scene.environment = null
      return
    }
    this.pmrem ??= new THREE.PMREMGenerator(this.renderer)
    this.envTarget = this.pmrem.fromScene(new RoomEnvironment(), 0.04)
    this.scene.environment = this.envTarget.texture
  }

  /** Multiplies IBL intensity on all content materials (simple exposure match to camera brightness). */
  setEnvironmentIntensity(intensity: number): void {
    this.scene.environmentIntensity = intensity
  }

  /**
   * The environment texture. Assign it to `material.envMap` when a material needs its *own*
   * `envMapIntensity` (three ignores per-material intensity for `scene.environment`).
   */
  get environmentTexture(): THREE.Texture | null {
    return this.envTarget?.texture ?? null
  }

  render(): void {
    this.renderer.render(this.scene, this.camera)
  }

  dispose(): void {
    this.envTarget?.dispose()
    this.pmrem?.dispose()
    this.renderer.dispose()
    this.canvas.remove()
  }
}

/** Apply a core Pose (camera space, cm) to a three.js object. */
export function applyPose(obj: THREE.Object3D, p: Pose): void {
  obj.position.set(p.position[0], p.position[1], p.position[2])
  obj.quaternion.set(p.rotation[0], p.rotation[1], p.rotation[2], p.rotation[3])
  obj.scale.setScalar(p.scale)
}
